import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { useSettings } from "../lib/settings";
import { unitSummary, TAX_NOTICE_ELEC, TAX_NOTICE_CASH, CONFIRMATION_DISCLAIMER } from "../lib/orderDisplay";

/**
 * Printable order form — shared by admin & retailer.
 * Backend enforces ownership: retailers only get their own, admin gets any.
 * Cash and E-Transfer/Cheque sections are always shown separately.
 */
export default function OrderPrint() {
  const { id } = useParams();
  const { settings } = useSettings();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  const currency = settings?.currency || "$";

  useEffect(() => {
    api.get(`/orders/${id}`)
      .then(r => setOrder(r.data))
      .catch(e => setErr(formatError(e.response?.data?.detail) || "Order not found"));
  }, [id]);

  if (err) return (
    <div className="min-h-screen bg-paper text-ink p-10">
      <div className="border border-soda p-6 max-w-lg" data-testid="order-print-error">{err}</div>
      <Link to="/" className="mt-4 inline-block underline">← Home</Link>
    </div>
  );
  if (!order) return <div className="p-10 text-sm">Loading…</div>;

  const created = new Date(order.created_at);
  const cashLines = (order.lines || []).filter(l => l.payment_method === "cash");
  const elecLines = (order.lines || []).filter(l => l.payment_method !== "cash");
  const cashTotal = order.cash_subtotal ?? cashLines.reduce((s, l) => s + l.line_total, 0);
  const elecTotal = order.electronic_subtotal ?? elecLines.reduce((s, l) => s + l.line_total, 0);

  const linesTable = (rows) => (
    <table className="w-full text-sm border border-ink">
      <thead>
        <tr className="border-b border-ink bg-black/5">
          <th className="text-left p-2 overline">Product</th>
          <th className="text-right p-2 overline">Qty</th>
          <th className="text-right p-2 overline">Unit</th>
          <th className="text-right p-2 overline">Line total</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((l, i) => (
          <tr key={i} className="border-b border-black/10 align-top">
            <td className="p-2">
              <div className="font-bold">{l.name}</div>
              {unitSummary(l) && <div className="text-[11px] text-black/60">{unitSummary(l)}</div>}
              {l.batches?.length > 0 && (
                <div className="mt-1 text-[10px] text-black/60">
                  Batches: {l.batches.map(b => `${b.batch_number || b.batch_id.slice(0, 6)} (${b.quantity}, exp ${b.expiry_date})`).join(" · ")}
                </div>
              )}
            </td>
            <td className="p-2 text-right">{l.quantity}</td>
            <td className="p-2 text-right">{currency}{Number(l.unit_price).toFixed(2)}</td>
            <td className="p-2 text-right font-bold">{currency}{Number(l.line_total).toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="bg-white text-ink min-h-screen">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4; margin: 12mm; }
        }
      `}</style>

      <div className="max-w-[820px] mx-auto p-6 md:p-10" data-testid="order-print">
        <div className="no-print flex justify-between mb-6 gap-3">
          <Link to="/" className="text-xs underline">← Home</Link>
          <div className="flex gap-2">
            <button onClick={() => window.print()} className="btn-primary text-xs" data-testid="order-print-btn">Print / Save PDF</button>
          </div>
        </div>

        <div className="border-b-2 border-ink pb-4 mb-6 flex justify-between items-start">
          <div>
            <div className="font-display text-3xl md:text-4xl uppercase leading-none">{settings?.business_name || "GTA Wholesale Supply"}</div>
            {settings?.address && <div className="text-xs mt-2 text-black/70">{settings.address}</div>}
            {settings?.phone && <div className="text-xs text-black/70">{settings.phone}</div>}
            {settings?.public_email && <div className="text-xs text-black/70">{settings.public_email}</div>}
          </div>
          <div className="text-right">
            <div className="overline text-black/60">Order Confirmation</div>
            <div className="font-display text-4xl md:text-5xl leading-none">#{order.inquiry_no}</div>
            <div className="text-xs text-black/60 mt-1">{created.toLocaleString()}</div>
            <div className="text-[10px] text-black/50 mt-1 uppercase tracking-widest">Not an invoice</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-6 text-sm">
          <div>
            <div className="overline text-black/60 mb-1">Bill to</div>
            <div className="font-bold">{order.business_name}</div>
            {order.contact_name && <div>{order.contact_name}</div>}
            {order.email && <div className="text-black/70">{order.email}</div>}
            {order.phone && <div className="text-black/70">{order.phone}</div>}
          </div>
          <div>
            <div className="overline text-black/60 mb-1">Delivery & status</div>
            <div className="capitalize"><b>Method:</b> {order.delivery_method}</div>
            <div><b>Status:</b> <span className="uppercase">{order.status}</span></div>
          </div>
        </div>

        {cashLines.length > 0 && (
          <section className="mb-6" data-testid="print-cash-section">
            <div className="flex items-center justify-between mb-2">
              <div className="overline">Cash items</div>
              <span className="inline-block border border-soda bg-soda text-paper px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">Cash only</span>
            </div>
            {linesTable(cashLines)}
            <div className="text-right mt-2">
              <span className="overline text-black/60 mr-3">Cash total</span>
              <span className="font-display text-2xl" data-testid="print-cash-total">{currency}{Number(cashTotal).toFixed(2)}</span>
            </div>
            <div className="text-[11px] text-black/60 text-right">{TAX_NOTICE_CASH}</div>
          </section>
        )}

        {elecLines.length > 0 && (
          <section className="mb-6" data-testid="print-elec-section">
            <div className="flex items-center justify-between mb-2">
              <div className="overline">E-Transfer / Cheque items</div>
              <span className="inline-block border border-ink bg-paper text-ink px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">E-Transfer / Cheque</span>
            </div>
            {linesTable(elecLines)}
            <div className="text-right mt-2">
              <span className="overline text-black/60 mr-3">E-Transfer / Cheque subtotal</span>
              <span className="font-display text-2xl" data-testid="print-elec-total">{currency}{Number(elecTotal).toFixed(2)}</span>
            </div>
            <div className="text-[11px] text-safety font-bold text-right mt-1" data-testid="print-elec-tax-notice">⚑ {TAX_NOTICE_ELEC}</div>
          </section>
        )}

        <div className="border-t-2 border-ink pt-3 text-right">
          <span className="overline text-black/60 mr-3">Combined total (before tax)</span>
          <span className="font-display text-3xl" data-testid="print-combined-total">{currency}{Number(order.subtotal || 0).toFixed(2)}</span>
        </div>

        {order.notes && (
          <div className="mt-6 text-sm">
            <div className="overline text-black/60 mb-1">Notes</div>
            <div className="border border-black/20 p-3 whitespace-pre-wrap">{order.notes}</div>
          </div>
        )}

        <div className="mt-8 border border-ink bg-secondary p-4 text-xs leading-relaxed" data-testid="print-disclaimer">
          {CONFIRMATION_DISCLAIMER}
        </div>

        <div className="mt-10 grid grid-cols-2 gap-8 text-xs">
          <div>
            <div className="border-t border-ink pt-2">Retailer signature</div>
          </div>
          <div>
            <div className="border-t border-ink pt-2">GTA Wholesale rep</div>
          </div>
        </div>
      </div>
    </div>
  );
}
